import moment from 'moment';
import * as dates from 'react-schedule-calendar/lib/utils/dates';
import { SlotEvent } from 'model/types';

export const TIME_DATE_FORMAT = 'HH:mm DD MMM';
export const DATE_TIME_FORMAT = 'DD-MMM-YY HH:mm';
export const TIME_FORMAT = 'HH:mm';
export const DATE_FORMAT = 'DDMMM';
export const DATE_FORMAT_SLASHED = 'DD/MM/YYYY';
export const DATE_FORMAT_SPACED = 'DD MMM YYYY';
export const DATE_FORMAT_DASHED = 'DD-MM-YYYY';
export const DATE_FORMAT_DASHED_SHORT = 'DD-MM-YY';
export const DATE_FORMAT_NO_YEAR = 'DD MMM';
export const DATE_TIME_FORMAT_PROGRESS = 'HH[h] mm[m]';
export const DATE_TIME_FORMAT_PROGRESS_FULL = 'HH[h] mm[m] ss[s]';
export const DATE_TIME_FORMAT_NORMALIZED = 'YYYY-MM-DDTHH:mm';
export const DATE_TIME_UTC_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

export const formatDate = (date: string | undefined, format: string): string =>
  date ? moment(date).format(format) : '';

export const dateToDateTimeString = (date: string): string =>
  formatDate(date, DATE_TIME_FORMAT);

export const dateToTimeDateString = (date: string): string =>
  formatDate(date, TIME_DATE_FORMAT);

export const dateToTimeString = (date: string): string =>
  formatDate(date, TIME_FORMAT);

export const dateToDateString = (date: string): string =>
  formatDate(date, DATE_FORMAT);

export const dateToMonthRange = (date = moment()) => {
  return {
    start: date.startOf('month').format(),
    end: date.endOf('month').format(),
  };
};

export const dateToUtcDateTimeString = (date: string): string =>
  moment.utc(date).format(DATE_TIME_UTC_FORMAT);

// TODO: fix spelling
export const lastWeekDayOfMounth = (
  year: number,
  month: number,
  day: number
): number => {
  const date = new Date(year, month, 1, 0);
  const weekday = date.getDay();
  const dayDiff = weekday === day ? 7 : (weekday - day + 7) % 7;
  return date.setDate(date.getDate() - dayDiff);
};

type Season = 'W' | 'S';

export const getValidEventRange = (
  season: Season,
  year: number
): {
  minDate: Date;
  maxDate: Date;
} => {
  let minDate: Date;
  let maxDate: Date;

  if (season === 'W') {
    minDate = new Date(lastWeekDayOfMounth(year, 10, 7));
    maxDate = new Date(lastWeekDayOfMounth(year + 1, 3, 6));
  }
  if (season === 'S') {
    minDate = new Date(lastWeekDayOfMounth(year, 3, 7));
    maxDate = new Date(lastWeekDayOfMounth(year, 10, 6));
  }

  return { minDate, maxDate };
};

type SeasonInfo = {
  season: Season;
  year: number;
};

export const getSeason = (date: Date): SeasonInfo => {
  let season: Season;
  let year: number;
  const dayDate = date.getDate();
  const dayMonth = date.getMonth();
  const dayYear = date.getFullYear();
  const fullDayDate = new Date(dayYear, dayMonth, dayDate).getTime();
  const lastSundayOfMarch = lastWeekDayOfMounth(dayYear, 3, 7);
  const lastSaturdayOfOcober = lastWeekDayOfMounth(dayYear, 10, 6);
  if (fullDayDate < lastSundayOfMarch) {
    year = dayYear - 1;
  } else {
    year = dayYear;
  }

  if (lastSundayOfMarch <= fullDayDate && fullDayDate <= lastSaturdayOfOcober) {
    season = 'S';
  } else {
    season = 'W';
  }
  return { season, year };
};

export const dateToUtcISOString = (date: Date): string => {
  const timeZoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timeZoneOffset).toISOString();
};

type EventAccessors = {
  start: (event: SlotEvent) => Date;
  end: (event: SlotEvent) => Date;
};

export const sortEvents = (
  evtA: SlotEvent,
  evtB: SlotEvent,
  accessors: EventAccessors
): number => {
  const startSort =
    +dates.startOf(accessors.start(evtA), 'hours') -
    +dates.startOf(accessors.start(evtB), 'hours');
  return startSort;
};

export const inRange = (
  e: SlotEvent,
  start: Date,
  end: Date,
  accessors: EventAccessors
): boolean => {
  const eStart = dates.startOf(accessors.start(e), 'day');
  const eEnd = accessors.end(e);
  const startsBeforeEnd = dates.lte(eStart, end, 'day'); // when the event is zero duration we need to handle a bit differently

  const endsAfterStart = !dates.eq(eStart, eEnd, 'minutes')
    ? dates.gte(eEnd, start, 'minutes')
    : false;
  return startsBeforeEnd && endsAfterStart;
};
